import React, { useMemo, useRef, useState, memo, forwardRef, useImperativeHandle, useCallback, useId } from "react"

const LazyReactPlayer = dynamic(() => import("react-player/lazy").then((m) => m.default), { ssr: false })

import styled from "styled-components"
import { gsap, useGSAP, ScrollTrigger } from "@repo/utils/gsap"
import useAnimationDebug from "../../animations/useAnimationDebug"
import { useWindowActive, useWindowWidth } from "../../hooks/useWindowSize"
import { useScrollContext } from "../Scroll"
import dynamic from "next/dynamic"
import Head from "next/head"

const Wrapper = styled.div`
	display: flex;
	width: 100%;
	max-width: 100vw;
	height: 100%;
	position: relative;
	z-index: 1;
	-webkit-backface-visibility: hidden;
	-webkit-transform: translateZ(0);
	-webkit-transform-style: preserve-3d;
	overflow: hidden;
	margin: 0;

	figure {
		//animation: fade-in var(--transition-duration) ease-in-out;
		display: flex;
		position: relative;
		//&:before {
		//	content: '';
		//	position: absolute;
		//	top: 0;
		//	left: 0;
		//	width: 100%;
		//	height: 100%;
		//}
	}

	video {
		object-fit: cover;
	}

	mux-player {
		position: absolute;
		inset: 0;
		--media-object-fit: cover;
		--media-object-size: cover;
		--media-object-position: center;
	}
`

export const VideoLoading = styled.div`
	--shine-color: rgba(211, 211, 211, 1);
	--shine-bg-color: transparent;
	display: flex;
	width: 100%;
	height: 100%;
	inset: 0;
	position: absolute;
	z-index: 1;
	justify-content: center;
	align-items: center;
	text-align: center;
	opacity: 0.8;
	background: linear-gradient(to right, var(--shine-bg-color) 0%, var(--shine-color) 50%, var(--shine-bg-color) 100%);
	background-size: 200% auto;
	animation: shine 1s ease-in-out infinite;
	transition: all var(--ease) var(--color-transition-duration);
	@keyframes shine {
		to {
			background-position: 200% center;
		}
	}

	&.hidden {
		visibility: hidden;
		opacity: 0;
		pointer-events: none;
	}
`
const ProgressBar = styled.span`
	--progress: 0;
	display: block;
	position: absolute;
	width: 90vw;
	height: 4px;
	//background-color: ${({ theme }) => theme.colors.gray};
	z-index: 1;
	margin: 0 5vw;
	bottom: 3vw;

	&:after {
		content: " ";
		position: absolute;
		inset: 0;
		background-color: ${({ theme }) => theme.colors.darkBlue};
		transform-origin: left;
		scale: var(--progress) 1;
	}
`

const Player = ({
	src,
	playsinline = true,
	muted = true,
	playing = false,
	controls,
	progress = false,
	fallback = null,
	preload = "metadata",
	priority = false,
	poster = null,
}) => {
	const PlayerRef = useRef()
	const ProgressBarRef = useRef()

	const config = {
		file: {
			attributes: {
				preload,
				poster,
			},
		},
		mux: {
			attributes: {
				preload,
			},
		},
	}

	if (src?.length <= 0) return null

	return (
		<>
			{poster && (
				<Head>
					<link rel="preload" as="image" href={poster} fetchpriority="high" />
				</Head>
			)}
			<LazyReactPlayer
				ref={PlayerRef}
				url={src}
				width="100%"
				height="100%"
				wrapper="figure"
				muted={muted}
				volume={muted ? 0 : 0.8}
				loop
				playsinline={playsinline}
				playing={playing}
				// playing={false}
				onPause={() => PlayerRef.current?.seekTo?.(0)}
				onProgress={(props) => {
					if (!progress) return
					const { played } = props
					if (played > 0.01) {
						gsap.to(ProgressBarRef.current, {
							"--progress": played,
							duration: 1,
							ease: "none",
						})
					} else {
						gsap.set(ProgressBarRef.current, {
							"--progress": played,
						})
					}
				}}
				fallback={fallback}
				controls={controls}
				config={config}
			/>
			{progress && (
				<>
					<ProgressBar ref={ProgressBarRef} />
				</>
			)}
		</>
	)
}

const Video = forwardRef(function Video(
	{
		id = "",
		src = "",
		className = "",
		autoplay = false,
		playing = undefined,
		children,
		muted = true,
		controls = false,
		playsinline = true,
		target = { current: undefined },
		setIsPlaying,
		progress = false,
		fallback = null,
		preload = "metadata",
		priority = false,
		loadWhenNear = true,
		poster = null,
		...props
	},
	fref,
) {
	const reactId = useId()
	const standardizedId = useMemo(() => {
		const base = `video-${id || reactId}`
		return base.replace(/--|-\s-/g, "-")
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]) //use passed id or generate one
	const WrapperRef = useRef(null)
	const [inViewport, setInViewport] = useState(false)
	const [hover, setHover] = useState(false)
	const vWidth = useWindowWidth()
	const windowActive = useWindowActive()
	const debug = useAnimationDebug()
	const hasUserScrolled = useScrollContext((s) => s.hasUserScrolled)
	const isPlaying = useMemo(() => {
		if (!windowActive) return false

		let state
		if (typeof playing !== "undefined") {
			state = playing
		} else {
			if (autoplay) {
				if (autoplay === "force") {
					state = true
				} else {
					state = inViewport
				}
			} else {
				state = hover
			}
		}
		if (typeof setIsPlaying === "function") {
			setIsPlaying(state)
		}
		return state
	}, [inViewport, windowActive, hover, autoplay, playing, setIsPlaying])

	const handlePointerEnter = useCallback(() => {
		setHover(true)
	}, [])

	const handlePointerLeave = useCallback(() => {
		setHover(false)
	}, [])

	useImperativeHandle(fref, () => WrapperRef.current, [])

	useGSAP(
		() => {
			const el = target?.current || WrapperRef.current
			if (autoplay && autoplay !== "force" && el) {
				ScrollTrigger.create({
					id: standardizedId,
					trigger: el,
					start: "top bottom+=100px",
					end: "bottom top",
					markers: debug,
					invalidateOnRefresh: true,
					onUpdate: ({ isActive }) => {
						setInViewport(isActive)
					},
				})
			}
		},
		{
			dependencies: [autoplay, standardizedId, vWidth, debug, target],
			revertOnUpdate: true,
		},
	)

	const shouldLoadVideo = !loadWhenNear ? true : hasUserScrolled

	return (
		<Wrapper
			id={standardizedId}
			ref={WrapperRef}
			className={`${className} video`}
			onPointerEnter={handlePointerEnter}
			onPointerLeave={handlePointerLeave}
			{...props}
		>
			{shouldLoadVideo && (
				<Player
					src={src}
					playing={isPlaying}
					muted={muted}
					playsinline={playsinline}
					controls={controls}
					progress={progress}
					fallback={fallback}
					preload={preload}
					priority={priority}
					poster={poster}
				/>
			)}
			{children}
		</Wrapper>
	)
})

export default memo(Video)